import React from 'react';
import { Button, Grid } from '@material-ui/core';
import {
  EntityApiDefinitionCard,
  EntityConsumedApisCard,
  EntityConsumingComponentsCard,
  EntityHasApisCard,
  EntityProvidedApisCard,
  EntityProvidingComponentsCard,
} from '@backstage/plugin-api-docs';
import { EntityKubernetesContent } from '@backstage/plugin-kubernetes';
import {
  EntityAboutCard,
  EntityDependsOnComponentsCard,
  EntityDependsOnResourcesCard,
  EntityHasComponentsCard,
  EntityHasResourcesCard,
  EntityHasSubcomponentsCard,
  EntityHasSystemsCard,
  EntityLayout,
  EntityLinksCard,
  EntitySwitch,
  EntityOrphanWarning,
  EntityProcessingErrorsPanel,
  isComponentType,
  isKind,
  hasCatalogProcessingErrors,
  isOrphan,
  hasRelationWarnings,
  EntityRelationWarning,
  isEntityWith,
  RelatedEntitiesCard
} from '@backstage/plugin-catalog';
import {
  EntityUserProfileCard,
  EntityGroupProfileCard,
  EntityMembersListCard,
  EntityOwnershipCard,
} from '@backstage/plugin-org';
import { EntityTechdocsContent } from '@backstage/plugin-techdocs';
import { EmptyState } from '@backstage/core-components';
import {
  Direction,
  EntityCatalogGraphCard,
} from '@backstage/plugin-catalog-graph';
import {
  ComponentEntity,
  Entity,
  RELATION_API_CONSUMED_BY,
  RELATION_API_PROVIDED_BY,
  RELATION_CHILD_OF,
  RELATION_CONSUMES_API,
  RELATION_DEPENDENCY_OF,
  RELATION_DEPENDS_ON,
  RELATION_HAS_PART,
  RELATION_PARENT_OF,
  RELATION_PART_OF,
  RELATION_PROVIDES_API,
} from '@backstage/catalog-model';

import { TechDocsAddons } from '@backstage/plugin-techdocs-react';
import { ReportIssue } from '@backstage/plugin-techdocs-module-addons-contrib';

import {
  HasProductLinesCard,
  ProductInfoCard,
  SubDomainEntityLayout,
  DomainEntityLayout,
  EnterpriseLayerEntityLayout,
  FunctionalDomainEntityLayout,
  ManageProductContent,
  ProductTeamEntityLayout
} from '@internal/plugin-ea-pack-react'
//import { EntitySnykContent } from 'backstage-plugin-snyk';
import { EntityTable } from '@backstage/plugin-catalog-react';

import {
  EntityAzurePipelinesContent,
  EntityAzurePullRequestsContent,
  EntityAzureReadmeCard,
  isAzureDevOpsAvailable,
} from '@backstage-community/plugin-azure-devops';

import {
  EntitySoundcheckContent,
  EntitySoundcheckCard,
} from '@spotify/backstage-plugin-soundcheck';

import { FeatureFlagged } from '@backstage/core-app-api';
import { featureFlagsApiRef, useApi, identityApiRef } from "@backstage/core-plugin-api"

import { SurveyEntityLayout } from '@internal/backstage-plugin-surveying';
import {
  techDebtEntityTabPath,
  techDebtEntityTabTitle,
  TechDebtComponentEntityContent,
  TechDebtSystemEntityContent,
  TechDebtEnterpriseLayerEntityContent,
  TechDebtFunctionalDomainEntityContent,
} from '@internal/backstage-plugin-techdebt';

const techdocsContent = (
  <EntityTechdocsContent>
    <TechDocsAddons>
      <ReportIssue />
    </TechDocsAddons>
  </EntityTechdocsContent>
);

// const cicdContent = (
//   // This is an example of how you can implement your company's logic in entity page.
//   // You can for example enforce that all components of type 'service' should use GitHubActions
//   <EntitySwitch>
//     <EntitySwitch.Case if={isGithubActionsAvailable}>
//       <EntityGithubActionsContent />
//     </EntitySwitch.Case>

//     <EntitySwitch.Case>
//       <EmptyState
//         title="No CI/CD available for this entity"
//         missing="info"
//         description="You need to add an annotation to your component if you want to enable CI/CD for it. You can read more about annotations in Backstage by clicking the button below."
//         action={
//           <Button
//             variant="contained"
//             color="primary"
//             href="https://backstage.io/docs/features/software-catalog/well-known-annotations"
//           >
//             Read more
//           </Button>
//         }
//       />
//     </EntitySwitch.Case>
//   </EntitySwitch>
// );

const entityWarningContent = (
  <>
    <EntitySwitch>
      <EntitySwitch.Case if={isOrphan}>
        <Grid item xs={12}>
          <EntityOrphanWarning />
        </Grid>
      </EntitySwitch.Case>
    </EntitySwitch>

    <EntitySwitch>
      <EntitySwitch.Case if={hasRelationWarnings}>
        <Grid item xs={12}>
          <EntityRelationWarning />
        </Grid>
      </EntitySwitch.Case>
    </EntitySwitch>

    <EntitySwitch>
      <EntitySwitch.Case if={hasCatalogProcessingErrors}>
        <Grid item xs={12}>
          <EntityProcessingErrorsPanel />
        </Grid>
      </EntitySwitch.Case>
    </EntitySwitch>
  </>
);

const isTechDebtEnabled = (_: Entity) =>
  useApi(featureFlagsApiRef).isActive('techdebt');

const overviewContent = (
  <Grid container spacing={3} alignItems="stretch">
    {entityWarningContent}
    <Grid item md={6}>
      <EntityAboutCard />
    </Grid>
    <Grid item md={6} xs={12}>
      <EntityCatalogGraphCard height={400} />
    </Grid>

    <Grid item md={4} xs={12}>
      <EntityLinksCard />
    </Grid>
    <Grid item md={8} xs={12}>
      <EntityHasSubcomponentsCard variant="gridItem" />
    </Grid>
  </Grid>
);

const serviceEntityPage = (
  <EntityLayout parentEntityRelations={["partOf"]}>
    <EntityLayout.Route path="/" title="Overview">
      {overviewContent}
    </EntityLayout.Route>

    <EntityLayout.Route path="/api" title="API">
      <Grid container spacing={3} alignItems="stretch">
        <Grid item md={6}>
          <EntityProvidedApisCard /> 
        </Grid>
        <Grid item md={6}>
          <EntityConsumedApisCard />
        </Grid>
      </Grid>
    </EntityLayout.Route>

    <EntityLayout.Route path="/dependencies" title="Dependencies">
      <Grid container spacing={3} alignItems="stretch">
        <Grid item md={6}>
          <EntityDependsOnComponentsCard variant="gridItem" />
        </Grid>
        <Grid item md={6}>
          <EntityDependsOnResourcesCard variant="gridItem" />
        </Grid>
      </Grid>
    </EntityLayout.Route>
    {/* <EntityLayout.Route path="/docs" title="Docs">
      {techdocsContent}
    </EntityLayout.Route> */}
    <EntityLayout.Route path="/kubernetes" title="Kubernetes">
      <EntityKubernetesContent refreshIntervalMs={30000} />
    </EntityLayout.Route>
    <EntityLayout.Route path={techDebtEntityTabPath} title={techDebtEntityTabTitle} if={isTechDebtEnabled}>
      <TechDebtComponentEntityContent />
    </EntityLayout.Route>
  </EntityLayout>
);

const websiteEntityPage = (
  <EntityLayout parentEntityRelations={["partOf"]}>
    <EntityLayout.Route path="/" title="Overview">
      {overviewContent}
    </EntityLayout.Route>
    <EntityLayout.Route path="/dependencies" title="Dependencies">
      <Grid container spacing={3} alignItems="stretch">
        <Grid item md={6}>
          <EntityDependsOnComponentsCard variant="gridItem" />
        </Grid>
        <Grid item md={6}>
          <EntityDependsOnResourcesCard variant="gridItem" />
        </Grid>
      </Grid>
    </EntityLayout.Route>

    {/* <EntityLayout.Route path="/docs" title="Docs">
      {techdocsContent}
    </EntityLayout.Route> */}
    {/* <EntityLayout.Route path="/snyk" title="Security (Snyk)">
      <EntitySnykContent />
    </EntityLayout.Route> */}
    <EntityLayout.Route path={techDebtEntityTabPath} title={techDebtEntityTabTitle} if={isTechDebtEnabled}>
      <TechDebtComponentEntityContent />
    </EntityLayout.Route>
  </EntityLayout>
);

/**
 * NOTE: This page is designed to work on small screens such as mobile devices.
 * This is based on Material UI Grid. If breakpoints are used, each grid item must set the `xs` prop to a column size or to `true`,
 * since this does not default. If no breakpoints are used, the items will equitably share the available space.
 * https://material-ui.com/components/grid/#basic-grid.
 */

const defaultEntityPage = (
  <EntityLayout parentEntityRelations={["partOf"]}>
    <EntityLayout.Route path="/" title="Overview">
      {overviewContent}
    </EntityLayout.Route>

    <EntityLayout.Route path="/docs" title="Docs">
      {techdocsContent}
    </EntityLayout.Route>
    <EntityLayout.Route path={techDebtEntityTabPath} title={techDebtEntityTabTitle} if={isTechDebtEnabled}>
      <TechDebtComponentEntityContent />
    </EntityLayout.Route>
  </EntityLayout>
);

const componentPage = (
  <EntitySwitch>
    <EntitySwitch.Case if={isComponentType('service')}>
      {serviceEntityPage}
    </EntitySwitch.Case>

    <EntitySwitch.Case if={isComponentType('website')}>
      {websiteEntityPage}
    </EntitySwitch.Case>

    <EntitySwitch.Case>{defaultEntityPage}</EntitySwitch.Case>
  </EntitySwitch>
);

const apiPage = (
  <EntityLayout parentEntityRelations={["partOf"]}>
    <EntityLayout.Route path="/" title="Overview">
      <Grid container spacing={3}>
        {entityWarningContent}
        <Grid item md={6}>
          <EntityAboutCard />
        </Grid>
        <Grid item md={6} xs={12}>
          <EntityCatalogGraphCard height={400} />
        </Grid>
        <Grid item md={4} xs={12}>
          <EntityLinksCard />
        </Grid>
        <Grid container item md={12}>
          <Grid item md={6}>
            <EntityProvidingComponentsCard />
          </Grid>
          <Grid item md={6}>
            <EntityConsumingComponentsCard />
          </Grid>
        </Grid>
      </Grid>
    </EntityLayout.Route>

    <EntityLayout.Route path="/definition" title="Definition">
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <EntityApiDefinitionCard />
        </Grid>
      </Grid>
    </EntityLayout.Route>
  </EntityLayout>
);

const userPage = (
  <EntityLayout>
    <EntityLayout.Route path="/" title="Overview">
      <Grid container spacing={3}>
        {entityWarningContent}
        <Grid item xs={12} md={6}>
          <EntityUserProfileCard />
        </Grid>
        <Grid item xs={12} md={6}>
          <EntityOwnershipCard />
        </Grid>
      </Grid>
    </EntityLayout.Route>
  </EntityLayout>
);

const groupPage = (
  <EntityLayout>
    <EntityLayout.Route path="/" title="Overview">
      <Grid container spacing={3}>
        {entityWarningContent}
        <Grid item xs={12} md={6}>
          <EntityGroupProfileCard />
        </Grid>
        <Grid item xs={12} md={6}>
          <EntityOwnershipCard />
        </Grid>
        <Grid item xs={12}>
          <EntityMembersListCard />
        </Grid>
      </Grid>
    </EntityLayout.Route>
    <EntityLayout.Route path="/soundcheck" title="Soundcheck" if={_ => useApi(featureFlagsApiRef).isActive("soundcheck")}>
      <EntitySoundcheckContent />
    </EntityLayout.Route>
  </EntityLayout>
);

const systemPage = (
  <EntityLayout parentEntityRelations={["partOf"]}>
    <EntityLayout.Route path="/" title="Overview">
      <Grid container spacing={3} alignItems="stretch">
        {entityWarningContent}
        <Grid item md={6}>
          <ProductInfoCard variant="gridItem" />
        </Grid>
        <Grid item md={6} xs={12}>
          <EntityCatalogGraphCard height={400} />
        </Grid>
        <FeatureFlagged with="soundcheck">
          <Grid item md={6} xs={12}>
            <EntitySoundcheckCard />
          </Grid>
        </FeatureFlagged>
        <Grid item md={6} xs={12}>
          <EntityLinksCard />
        </Grid>
        <Grid item md={6} xs={12}>
          <EntityHasComponentsCard variant="gridItem" />
        </Grid>
        <Grid item md={6} xs={12}>
          <EntityHasResourcesCard variant="gridItem" />
        </Grid>
        <Grid item md={12} xs={12}>
          <EntityHasApisCard variant="gridItem" />
        </Grid>
      </Grid>
    </EntityLayout.Route>
    <EntityLayout.Route path="/diagram" title="Diagram">
      <EntityCatalogGraphCard
        direction={Direction.TOP_BOTTOM}
        title="System Diagram"
        height={700}
        relations={[
          RELATION_PART_OF,
          RELATION_HAS_PART,
          RELATION_API_CONSUMED_BY,
          RELATION_API_PROVIDED_BY,
          RELATION_CONSUMES_API,
          RELATION_PROVIDES_API,
          RELATION_DEPENDENCY_OF,
          RELATION_DEPENDS_ON,
        ]}
        unidirectional={false}
      />
    </EntityLayout.Route>
    {/* <EntityLayout.Route path="/business-capabilities" title="Business Capabilities">
      <EntityCatalogGraphCard
        variant="gridItem"
        direction={Direction.BOTTOM_TOP}
        title="Business Capabilities"
        height={700}
        relations={[
          "delivers",
          RELATION_PARENT_OF

        ]}
        unidirectional={false}
        kinds={["System", "BusinessCapability"]}
        maxDepth={3}
        mergeRelations={false}

      />
    </EntityLayout.Route> */}
    <EntityLayout.Route path="/manage" title="Manage">
      <ManageProductContent />
    </EntityLayout.Route>
    <EntityLayout.Route path={techDebtEntityTabPath} title={techDebtEntityTabTitle} if={isTechDebtEnabled}>
      <TechDebtSystemEntityContent />
    </EntityLayout.Route>
    <EntityLayout.Route path="/soundcheck" title="Gemcheck" if={_ => useApi(featureFlagsApiRef).isActive("gemcheck")}>
      <EntitySoundcheckContent />
    </EntityLayout.Route>
  </EntityLayout>
);

const adoProjectEntityPage = (
  <EntityLayout parentEntityRelations={["partOf"]}>
    <EntityLayout.Route path="/" title="Overview">
      <Grid container spacing={3} alignItems="stretch">
        {entityWarningContent}
        <Grid item md={6}>
          <EntityAboutCard />
        </Grid>
        <Grid item md={6}>
          <EntityHasResourcesCard variant="gridItem" title="Repositories" columns={[
            EntityTable.columns.createEntityRefColumn({ defaultKind: "resource" }),
            EntityTable.columns.createSystemColumn()
          ]} />
        </Grid>
      </Grid>
    </EntityLayout.Route>
    <EntityLayout.Route if={isAzureDevOpsAvailable} path="/pipelines" title="Pipelines">
      <EntityAzurePipelinesContent defaultLimit={25} />
    </EntityLayout.Route>
  </EntityLayout>
);

const asComponentEntities = (entities: Entity[]): ComponentEntity[] => entities as ComponentEntity[];

const adoRepoEntityPage = (
  <EntityLayout parentEntityRelations={["partOf"]}>
    <EntityLayout.Route path="/" title="Overview">
      <Grid container spacing={3} alignItems="stretch">
        {entityWarningContent}
        <Grid item md={6}>
          <EntityAboutCard />
        </Grid>
        <Grid item md={6} xs={12}>
          <EntityCatalogGraphCard height={300} />
        </Grid>
        <Grid item md={6} xs={12}>
          <EntityAzureReadmeCard maxHeight={450} />
        </Grid>
        <Grid item md={6} xs={12}>
          <RelatedEntitiesCard
            variant="gridItem"
            title="Components"
            entityKind={"component"}
            relationType={"containsSourceCodeFor"}
            columns={[
              EntityTable.columns.createEntityRefColumn({ defaultKind: "resource" }),
              EntityTable.columns.createSystemColumn()
            ]}
            asRenderableEntities={asComponentEntities}
            emptyMessage="No Product Lines or Sub Domains are part of this domain"
            emptyHelpLink={""}
          />
        </Grid>
      </Grid>
    </EntityLayout.Route>
    <EntityLayout.Route if={isAzureDevOpsAvailable} path="/pipelines" title="Pipelines">
      <EntityAzurePipelinesContent defaultLimit={25} />
    </EntityLayout.Route>
    <EntityLayout.Route if={isAzureDevOpsAvailable} path="/pull-requests" title="Pull Requests">
      <EntityAzurePullRequestsContent defaultLimit={25} />
    </EntityLayout.Route>
  </EntityLayout>
);

const surveyEntityPage = (
  <EntityLayout>
    <EntityLayout.Route path="/" title="Overview">
      <SurveyEntityLayout />
    </EntityLayout.Route>
  </EntityLayout>
);

type DomainLayoutProps = {
  gemcheckCard?: React.ReactNode;
  children?: React.ReactNode;
};

const isNonProductTeamGroup = (entity: Entity) =>
  entity.kind === 'Group' && entity.spec?.type !== 'ProductTeam';

const isProductTeamGroup = (entity: Entity) =>
  entity.kind === 'Group' && entity.spec?.type === 'ProductTeam';

const renderGemcheckRoute = () => (
  <EntityLayout.Route path="/soundcheck" title="Gemcheck" if={_ => useApi(featureFlagsApiRef).isActive('gemcheck')}>
    <EntitySoundcheckContent />
  </EntityLayout.Route>
);

const renderDomainEntityLayout = (
  LayoutComponent: React.ComponentType<DomainLayoutProps>,
  techDebtContent?: React.ReactNode,
) => (
  <LayoutComponent gemcheckCard={<EntitySoundcheckCard title="Gemcheck" />}>
    {techDebtContent}
    {renderGemcheckRoute()}
  </LayoutComponent>
);

export const entityPage = (
  <EntitySwitch>
    <EntitySwitch.Case if={isKind('component')} children={componentPage} />
    <EntitySwitch.Case if={isKind('api')} children={apiPage} />
    <EntitySwitch.Case if={isNonProductTeamGroup} children={groupPage} />
    <EntitySwitch.Case if={isProductTeamGroup}><ProductTeamEntityLayout /></EntitySwitch.Case>
    <EntitySwitch.Case if={isKind('user')} children={userPage} />
    <EntitySwitch.Case if={isKind('system')} children={systemPage} />
    <EntitySwitch.Case if={isEntityWith({ kind: 'domain', type: 'Domain' })}>
      {renderDomainEntityLayout(DomainEntityLayout)}
    </EntitySwitch.Case>
    <EntitySwitch.Case if={isEntityWith({ kind: 'domain', type: 'EnterpriseLayer' })}>
      {renderDomainEntityLayout(
        EnterpriseLayerEntityLayout,
        <EntityLayout.Route path={techDebtEntityTabPath} title={techDebtEntityTabTitle} if={isTechDebtEnabled}>
          <TechDebtEnterpriseLayerEntityContent />
        </EntityLayout.Route>,
      )}
    </EntitySwitch.Case>
    <EntitySwitch.Case if={isEntityWith({ kind: 'domain', type: 'FunctionalDomain' })}>
      {renderDomainEntityLayout(
        FunctionalDomainEntityLayout,
        <EntityLayout.Route path={techDebtEntityTabPath} title={techDebtEntityTabTitle} if={isTechDebtEnabled}>
          <TechDebtFunctionalDomainEntityContent />
        </EntityLayout.Route>,
      )}
    </EntitySwitch.Case>
    <EntitySwitch.Case if={isEntityWith({ kind: 'domain', type: ['SubDomain', 'ProductLine'] })}>
      {renderDomainEntityLayout(SubDomainEntityLayout)}
    </EntitySwitch.Case>
    {/* <EntitySwitch.Case if={isKind('businesscapability')} ><BusinessCapabilityEntityLayout /></EntitySwitch.Case> */}
    <EntitySwitch.Case if={isEntityWith({ kind: "resource", type: ["ADO Project"] })} children={adoProjectEntityPage} />
    <EntitySwitch.Case if={isEntityWith({ kind: "resource", type: ["ADO Repository"] })} children={adoRepoEntityPage} />
    <EntitySwitch.Case if={isKind('survey')} children={surveyEntityPage} />

    <EntitySwitch.Case>{defaultEntityPage}</EntitySwitch.Case>
  </EntitySwitch>
);
